import { z } from "zod";

export const PartnerSchema = z.object({
    PartnerName: z.string().min(1, "Partner Name is Required"),
    ID: z.number().optional(),
    Description: z.string().optional(),
    PartnerType: z.string().optional(),
    DateCreated: z.string(),
    Modifieddate: z.string().nullable(),
    PartnerSubType: z
        .union([z.number(), z.string()])
        .optional().nullable(),
    PartnerSubName: z
        .union([z.number(), z.string()])
        .optional().nullable(),
    RecordNumber: z.string().optional(),
    SelectedOptions: z.array(z.any()).optional(),
    PermissionToDecrypt: z.boolean().optional(),
});

export const AddPartnerSchema = PartnerSchema.omit({
    DateCreated: true,
    Modifieddate: true,
});

export type PartnerForm = z.infer<typeof PartnerSchema>;
export type AddPartnerForm = z.infer<typeof AddPartnerSchema>;

export interface PartnerData {
    items: PartnerForm[];
    page_no: number;
    page_size: number;
    total: number;
    total_pages: number;
}

// ==================== OFFER SCHEMAS ====================

export const OfferSchema = z.object({
    Id: z.number().optional().nullable(),
    ExchangeOfferName: z.string().min(1, "Offer Name is required"),
    ExPartnerId: z.number().optional(),
    ExPartnerName: z.array(z.string()).optional(),
    OfferDetails: z.string().optional(),
    CreatedDate: z.string().optional(),
    ModifiedDate: z.string().nullable().optional(),
    IsActive: z.boolean().optional(),
    ReferenceFormId: z.number().optional()
});

export type OfferForm = z.infer<typeof OfferSchema>;

export const AddOfferSchema = z.object({
    ExPartnerId: z
        .array(z.number())
        .min(1, "Please select at least one partner"),
    ExchangeOfferName: z.string().min(1, "Offer name is required"),
    OfferDetails: z.string().optional(),
});

export type AddOfferForm = z.infer<typeof AddOfferSchema>;

export interface OfferData {
    items: OfferForm[];
    page: number;
    pageSize: number;
    total: number;
    pages: number;
}
// ==================== Indivisual OFFER SCHEMAS ====================

export const IndividualOfferSchema = z.object({
    Id: z.number().optional().nullable(),
    ExchangeOfferName: z.string().min(1, "Offer Name is required"),
    OfferType: z.string().optional(),
    OfferDetails: z.string().optional(),
    CreatedDate: z.string().optional(),
    ModifiedDate: z.string().nullable().optional(),
    ValidFrom: z.string().optional(),
    ValidTo: z.string().optional(),
    IsActive: z.boolean().optional(),
    ReferenceFormId: z.number().optional()
});

export type IndividualOfferForm = z.infer<typeof IndividualOfferSchema>;

export const AddIndividualOfferSchema = z.object({
    ExchangeOfferName: z.string().min(1, "Offer name is required"),
    OfferType: z.string().min(1, "Offer type is required"),
    OfferDetails: z.string().optional(),
    ValidFrom: z.string().min(1, "Required"),
    ValidTo: z.string().min(1, "Required"),
}).refine(
    (data) => !data.ValidFrom || !data.ValidTo || data.ValidTo >= data.ValidFrom,
    {
        message: "Valid To must be on or after Valid From",
        path: ["ValidTo"],  // error shows under ValidTo field
    }
);

export type AddIndividualOfferForm = z.infer<typeof AddIndividualOfferSchema>;

export interface IndividualOfferData {
    items: IndividualOfferForm[];
    page: number;
    pageSize: number;
    total: number;
    pages: number;
}

// ==================== ADDITIONAL OFFER SCHEMAS ====================

export const AdditionalOfferSchema = z.object({
    Id: z.number().optional().nullable(),
    ParrentId: z.number().optional().nullable(),
    ExchangeOfferName: z.string().min(1, "Additional Offer Name is required"),
    CreatedBy: z.string().nullable().optional(),
    CreatedDate: z.string().optional(),
    ModifiedDate: z.string().nullable().optional(),
    AdditionalOfferDetails: z.string().optional(),
    ReferenceFormId: z.number().optional()
});

export const AddAdditionalOfferSchema = AdditionalOfferSchema.omit({
    CreatedDate: true,
    ModifiedDate: true,
});

export type AdditionalOfferForm = z.infer<typeof AdditionalOfferSchema>;
export type AddAdditionalAddOfferForm = z.infer<typeof AddAdditionalOfferSchema>;

export interface AdditionalOfferData {
    items: AdditionalOfferForm[];
    page: number;
    pageSize: number;
    total: number;
    pages: number;
}

// ==================== RULE SCHEMAS ====================

export const RuleSchema = z.object({
    Rule_Id: z.number(),
    ObjectId: z.number().nullable(),
    Version: z.number().nullable(),
    DateTimeChanged: z.string(),
    Approved: z.number().nullable(),

    Name: z.string(),
    Type: z.number(),

    ObjectName: z.string().nullable(),
    ObjectVersionId: z.number().nullable(),

    raw_rule: z.string(),
    rule_with_value: z.string().nullable(),
});

export type RuleForm = z.infer<typeof RuleSchema>;

export interface RuleData {
    items: RuleForm[];
    page: number;
    pageSize: number;
    total: number;
}

// ==================== RULE CONDITION TYPES ====================

export interface RuleCondition {
    id: string;
    dataType: string;
    attribute: string;
    operator: string;
    valueType: string;
    dataType2?: string;
    attribute2?: string;
    value?: string;
    type?: "subgroup";
    conditions?: RuleCondition[];
    logicalOperators?: string[];
}

export interface RuleGroup {
    id: string;
    type: "group";
    conditions: RuleCondition[];
    logicalOperators: string[];
}

export interface RawRuleJson {
    groups: (RuleGroup | RuleCondition)[];
    groupOperators: string[];
}

export interface RulePricing {
    Id: number;
    Rule_Id: number;
    Data_Type: string;
    Attribute_Name: string;
    Operator: string;
    Value_Type: string;
    Expression: string;
    ExpressionJson: string;
    Exp_DataType: string | null;
    Exp_Attribute: string | null;
    Exp_Value: string | null;
    StartGroup: string | null;
    EndGroup: string | null;
    RootValue: string | null;
    RootDataType: string | null;
    RootAttribute: string | null;
}

export interface RuleComposeResponse {
    Rule_Id: number;
    RuleType: number;
    RuleExpression: string;
    RuleExpressionJson: string;
    RawRuleJson: RawRuleJson;
    AiRuleExpression: string;
    pricing: RulePricing[];
}

// ==================== ATTRIBUTE & DATATYPE TYPES ====================

export interface Attribute {
    ID: number;
    DataTypeId: number;
    name: string;
    type: number;
    label: string;
    cm_values: string | null;
    IsRequired: boolean;
    DropdownKind: string;
    ParentAttributeCode: string | null;
    SortOrder: number;
    CreatedAt: string;
    Link: string | null;
}

export interface AttributeResponse {
    datatypeId: number;
    typeName: string;
    count: number;
    items: Attribute[];
}

export interface DataTypeItem {
    Name: string;
    DataLabel: string;
    Version: number;
    XsdSchema: string;
    IsActive: boolean | null;
    ApplicationScope: string | null;
    ApplicationID: number | null;
    ID: number;
    CreatedDateTime: string;
    hasAttributes: boolean;
}

export interface DataTypeResponse {
    items: DataTypeItem[];
    total: number;
    page: number;
    pageSize: number;
}

// ==================== MODIFY RULE TYPES ====================

export interface ModifyRule {
    Data: string;
    ObjectId: null;
    Remarks: string;
    Rule_Id: number;
    OfferId: number;
}

export interface ToggleData {
    Id: number;
    Rule_Id: number;
    OfferId: number;
    Version: number;
    Data: string;
    ObjectId: number | null;
    Approved: number;
    DisplaySequence: number | null;
    DateTimeChanged: string;
    Remarks: string;
}

// ==================== API RESPONSE TYPE ====================

export interface ApiResponse<T> {
    data: T;
    message: string;
    success: boolean;
}